import axios from 'axios';
import { envConfig, debugLog } from '../config/env';

const api = axios.create({
  baseURL: envConfig.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器 - 添加调试日志
api.interceptors.request.use(
  (config) => {
    debugLog('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      params: config.params,
      data: config.data
    });
    return config;
  },
  (error) => {
    debugLog('API Request Error:', error);
    return Promise.reject(error);
  }
);

// 响应拦截器 - 添加调试日志和错误处理
api.interceptors.response.use(
  (response) => {
    debugLog('API Response:', {
      status: response.status,
      url: response.config.url,
      data: response.data
    });
    return response;
  },
  (error) => {
    debugLog('API Response Error:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      data: error.response?.data
    });
    
    if (error.response) {
      const { status } = error.response;
      switch (status) {
        case 401:
          console.error('未授权访问');
          break;
        case 403:
          console.error('禁止访问');
          break;
        case 404:
          console.error('资源不存在');
          break;
        case 500:
          console.error('服务器错误');
          break;
        default:
          console.error('请求失败:', error.message);
      }
    } else if (error.request) {
      console.error('网络请求失败 - 请检查网络连接和API服务器状态');
    } else {
      console.error('请求配置错误:', error.message);
    }
    return Promise.reject(error);
  }
);

// 邮件相关接口
export interface Email {
  id: number;
  message_id: string;
  subject: string;
  sender_email: string;
  sender_name?: string;
  recipient_email: string;
  content: string;
  translated_content?: string;
  summary?: string;
  category: string;
  status: string;
  email_type: string;
  priority: number;
  confidence_score: number;
  auto_reply_content?: string;
  sent_at?: string;
  received_at: string;
  replied_at?: string;
  tags?: string;
}

export interface EmailListResponse {
  emails: Email[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface EmailStats {
  total_emails: number;
  unread_emails: number;
  read_emails: number;
  user_emails: number;
  non_user_emails: number;
  today_received: number;
  today_sent: number;
  categories: Record<string, number>;
  service_stats: {
    running: boolean;
    stats: {
      emails_processed: number;
      emails_sent: number;
      errors: number;
      last_check: string;
      average_processing_time: number;
      active_connections: number;
    };
    active_tasks: number;
    config: {
      check_interval: number;
      batch_size: number;
      timeout: number;
    };
  };
}

export interface EmailAccount {
  id: number;
  email: string;
  imap_server: string;
  imap_port: number;
  smtp_server: string;
  smtp_port: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// 邮件API
export const fetchEmails = async (params: {
  skip?: number;
  limit?: number;
  status?: string;
  category?: string;
  account_id?: number;
  search?: string;
}): Promise<EmailListResponse> => {
  const response = await api.get('/emails', { params });
  return response.data;
};

export const fetchEmailStats = async (): Promise<EmailStats> => {
  const response = await api.get('/emails/stats');
  return response.data;
};

export const fetchEmail = async (id: number): Promise<Email> => {
  const response = await api.get(`/emails/${id}`);
  return response.data;
};

export const updateEmail = async (id: number, data: {
  status?: string;
  tags?: string;
  priority?: number;
}): Promise<any> => {
  const params = new URLSearchParams();
  if (data.status) params.append('status', data.status);
  if (data.tags) params.append('tags', data.tags);
  if (data.priority) params.append('priority', data.priority.toString());
  
  const response = await api.put(`/emails/${id}/status?${params.toString()}`);
  return response.data;
};

export const replyEmail = async (id: number, content: string): Promise<any> => {
  const response = await api.post(`/emails/${id}/reply`, { reply_content: content });
  return response.data;
};

// 账户API
export const fetchAccounts = async (): Promise<{accounts: EmailAccount[], total: number}> => {
  const response = await api.get('/accounts');
  return response.data;
};

export const createAccount = async (data: {
  email: string;
  password: string;
  imap_server: string;
  imap_port: number;
  smtp_server: string;
  smtp_port: number;
}): Promise<EmailAccount> => {
  const response = await api.post('/accounts', data);
  return response.data;
};

export const updateAccount = async (id: number, data: Partial<EmailAccount>): Promise<EmailAccount> => {
  const response = await api.put(`/accounts/${id}`, data);
  return response.data;
};

export const deleteAccount = async (id: number): Promise<any> => {
  const response = await api.delete(`/accounts/${id}`);
  return response.data;
};

export const testAccountConnection = async (id: number): Promise<any> => {
  const response = await api.post(`/accounts/${id}/test`);
  return response.data;
};

export const toggleAccountStatus = async (id: number): Promise<any> => {
  const response = await api.post(`/accounts/${id}/toggle`);
  return response.data;
};

// AI分析相关接口
export interface AIEmailAnalysis {
  id: number;
  email_id: number;
  translated_content?: string;
  chinese_translation?: string;  // 新增：中文翻译
  summary?: string;
  chinese_summary?: string;  // 新增：中文摘要
  category?: string;
  sentiment?: string;
  priority_score?: number;
  tags: string[];
  suggested_reply?: string;
  reply_chinese_translation?: string;  // 新增：回复中文翻译
  reply_tone?: string;
  confidence_scores: Record<string, number>;
  created_at: string;
}

export interface AITask {
  id: number;
  assistant_id: number;
  task_type: string;
  status: string;
  input_data: Record<string, any>;
  output_data: Record<string, any>;
  email_id?: number;
  thread_id?: number;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  created_at: string;
}

// AI分析API
export const fetchEmailAnalysis = async (emailId: number): Promise<AIEmailAnalysis> => {
  const response = await api.get(`/ai/email-analysis/${emailId}`);
  return response.data;
};

export const fetchEmailTasks = async (emailId: number): Promise<AITask[]> => {
  const response = await api.get(`/ai/tasks?email_id=${emailId}`);
  return response.data;
};

// 触发邮件AI分析
export const triggerEmailAnalysis = async (emailId: number): Promise<{message: string}> => {
  const response = await api.post(`/ai/trigger-assistant/1`, { email_id: emailId });
  return response.data;
};

// 聊天室相关API方法
export const updateChatRoomTags = async (userEmail: string, tags: string[]) => {
  // 首先获取该用户的发件人规则
  const rulesResponse = await api.get(`/sender-rules?search=${encodeURIComponent(userEmail)}`);
  const rules = rulesResponse.data.rules;
  
  if (!rules || rules.length === 0) {
    throw new Error(`未找到用户 ${userEmail} 的发件人规则`);
  }
  
  // 找到匹配的规则
  const rule = rules.find((r: any) => r.sender_email === userEmail && r.rule_type === 'user');
  if (!rule) {
    throw new Error(`未找到用户 ${userEmail} 的用户类型规则`);
  }
  
  // 使用通用的更新接口
  const response = await api.put(`/sender-rules/${rule.id}`, {
    tags: tags.join(', ')
  });
  return response.data;
};

export const updateChatRoomUserId = async (userEmail: string, userId: string) => {
  // 首先获取该用户的发件人规则
  const rulesResponse = await api.get(`/sender-rules?search=${encodeURIComponent(userEmail)}`);
  const rules = rulesResponse.data.rules;
  
  if (!rules || rules.length === 0) {
    throw new Error(`未找到用户 ${userEmail} 的发件人规则`);
  }
  
  // 找到匹配的规则
  const rule = rules.find((r: any) => r.sender_email === userEmail && r.rule_type === 'user');
  if (!rule) {
    throw new Error(`未找到用户 ${userEmail} 的用户类型规则`);
  }
  
  // 使用通用的更新接口
  const response = await api.put(`/sender-rules/${rule.id}`, {
    user_id: userId
  });
  return response.data;
};

export const updateChatRoom = async (userEmail: string, updates: any) => {
  const response = await api.put(`/email-chat/rooms/${userEmail}`, updates);
  return response.data;
};

// 数据同步API
export interface SyncStatus {
  total_senders: number;
  total_rules: number;
  user_senders: number;
  total_chat_rooms: number;
  total_replies: number;
  rules_synced: boolean;
  chat_rooms_synced: boolean;
}

export interface SyncResponse {
  success: boolean;
  message: string;
  synced_count: number;
}

export const getSyncStatus = async (): Promise<SyncStatus> => {
  const response = await api.get('/sync/status');
  return response.data.data;
};

export const triggerSync = async (): Promise<SyncResponse> => {
  const response = await api.post('/sync/senders');
  return response.data;
};

// 用户查询相关接口
export interface UserLookupResponse {
  found: boolean;
  user_id: string | null;
  user_info: {
    id: number;
    name: string;
    nick_name: string;
    email: string;
    provider: string;
    locale: string;
    created_at: string;
    updated_at: string;
  } | null;
  message: string;
}

export const lookupUserByEmail = async (email: string): Promise<UserLookupResponse> => {
  const response = await api.get(`/sender-rules/user-lookup/${encodeURIComponent(email)}`);
  return response.data;
};

// 发件人规则相关接口
export interface SenderRuleResponse {
  rules: any[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export const fetchSenderRules = async (params: {
  rule_type?: string;
  active_only?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  limit?: number;
}): Promise<SenderRuleResponse> => {
  const response = await api.get('/sender-rules', { params });
  return response.data;
};

export const updateSenderRule = async (ruleId: number, data: any): Promise<any> => {
  const response = await api.put(`/sender-rules/${ruleId}`, data);
  return response.data;
};

export const batchUpdateUserIds = async (rules: { id: number; email: string }[]): Promise<{
  success: number;
  failed: number;
  results: Array<{ id: number; email: string; success: boolean; user_id?: string; error?: string }>;
}> => {
  const results = [];
  let success = 0;
  let failed = 0;

  for (const rule of rules) {
    try {
      // 查询用户ID
      const userLookup = await lookupUserByEmail(rule.email);
      
      if (userLookup.found && userLookup.user_id) {
        // 更新发件人规则
        await updateSenderRule(rule.id, { user_id: userLookup.user_id });
        results.push({
          id: rule.id,
          email: rule.email,
          success: true,
          user_id: userLookup.user_id
        });
        success++;
      } else {
        results.push({
          id: rule.id,
          email: rule.email,
          success: false,
          error: '未找到用户ID'
        });
        failed++;
      }
    } catch (error) {
      results.push({
        id: rule.id,
        email: rule.email,
        success: false,
        error: error instanceof Error ? error.message : '未知错误'
      });
      failed++;
    }
  }

  return { success, failed, results };
};

// 为了向后兼容，也导出api实例作为apiService
export { api as apiService }; 